import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axiosInstance from "../../../utils/axios/axiosInstance";
import { FaChevronLeft, FaChevronRight, FaHeart, FaRegHeart, FaMapMarkerAlt, FaPhone, FaEnvelope, FaSearchPlus, FaTimes, FaCheckCircle, FaClock, FaUsers, FaBell, FaExclamationCircle, FaMoneyBillWave, FaCreditCard, FaPaypal, FaMobileAlt, FaCalendarAlt, FaUser  } from 'react-icons/fa';
import { toast } from 'react-toastify';
import loadingGif from '../../../assets/images/loading.gif';

const FavoritesDetailsPage = () => {
  const { id, slug } = useParams();
  const navigate = useNavigate();

  const createSlug = useCallback((title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  }, []);

  // Helper function to get current time in HH:MM format
  const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Helper function to get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Use ref to track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  const pollIntervalRef = useRef(null);

  // Helper function to get item condition with fallback options
  const getItemCondition = useCallback((item) => {
    if (!item) return 'Not specified';
    
    // First check for readable condition names
    const conditionName = item.condition_name || 
                         item.conditionName ||
                         item.condition_text ||
                         item.conditionText;
    
    if (conditionName && typeof conditionName === 'string' && conditionName.trim()) {
      const readableName = conditionName.trim();
      console.log('✅ Found readable condition name:', readableName);
      return readableName.charAt(0).toUpperCase() + readableName.slice(1).toLowerCase();
    }
    
    // Then check for condition IDs that need mapping
    const conditionValue = item.condition || 
                          item.item_condition || 
                          item.product_condition || 
                          item.ad_condition ||
                          item.state ||
                          item.item_state ||
                          item.condition_id ||
                          item.conditionId;
    
    // Enhanced debug logging for condition field
    console.log('🔍 Condition Debug Info:', {
      conditionValue,
      type: typeof conditionValue,
      condition_name: item.condition_name,
      conditionName: item.conditionName,
      condition: item.condition,
      condition_id: item.condition_id,
      conditionId: item.conditionId,
      state: item.state,
      allItemKeys: Object.keys(item)
    });
    
    // Enhanced condition mapping with more possibilities
    if (typeof conditionValue === 'number' || (typeof conditionValue === 'string' && !isNaN(parseInt(conditionValue)))) {
      const numericValue = parseInt(conditionValue);
      
      // Multiple mapping possibilities based on common condition ID patterns
      const conditionMaps = [
        // Standard 1-5 mapping
        {
          1: 'New',
          2: 'Like New', 
          3: 'Good',
          4: 'Fair',
          5: 'Poor',
          0: 'Not specified'
        },
        // Alternative 0-4 mapping
        {
          0: 'New',
          1: 'Like New',
          2: 'Good', 
          3: 'Fair',
          4: 'Poor'
        },
        // Reverse 5-1 mapping (some systems use reverse)
        {
          5: 'New',
          4: 'Like New',
          3: 'Good',
          2: 'Fair', 
          1: 'Poor'
        },
        // Extended mapping for systems with more conditions
        {
          1: 'Brand New',
          2: 'Excellent',
          3: 'Very Good',
          4: 'Good',
          5: 'Fair',
          6: 'Poor',
          7: 'For Parts',
          8: 'Damaged',
          0: 'Not specified'
        },
        // Another common pattern
        {
          10: 'New',
          20: 'Like New',
          30: 'Good',
          40: 'Fair',
          50: 'Poor'
        }
      ];
      
      // Try each mapping until we find a match
      for (const map of conditionMaps) {
        if (map[numericValue]) {
          console.log(`✅ Condition mapped: ${numericValue} -> ${map[numericValue]}`);
          return map[numericValue];
        }
      }
      
      // If no mapping found, return a generic description
      console.log(`⚠️ No condition mapping found for value: ${numericValue}`);
      return `Condition ${numericValue}`;
    }
    
    // If it's already a readable string, return it with proper capitalization
    if (typeof conditionValue === 'string' && conditionValue.trim()) {
      const trimmedValue = conditionValue.trim();
      
      // Check if it's already a readable condition (not just a number as string)
      const readableConditions = ['new', 'like new', 'excellent', 'very good', 'good', 'fair', 'poor', 'used', 'refurbished'];
      const isReadableCondition = readableConditions.some(condition => 
        trimmedValue.toLowerCase().includes(condition)
      );
      
      if (isReadableCondition || trimmedValue.length > 2) {
        return trimmedValue.charAt(0).toUpperCase() + trimmedValue.slice(1).toLowerCase();
      } else {
        // If it's just a number as string, try to parse it
        const numericValue = parseInt(trimmedValue);
        if (!isNaN(numericValue)) {
          return `Condition ${numericValue}`;
        }
      }
    }
    
    console.log('❌ No condition value found, returning default');
    return 'Not specified';
  }, []);

  // FIXED: Move normalizeStatus function to the top and make it more robust
  const normalizeStatus = useCallback((status) => {
    if (!status) return 'pending';
    const normalized = status.toString().toLowerCase().trim();
    
    // Handle variations in status values
    if (normalized === 'accept' || normalized === 'accepted' || normalized === 'approved' || normalized === '1') return 'accepted';
    if (normalized === 'reject' || normalized === 'rejected' || normalized === '2') return 'rejected';
    if (normalized === 'pending' || normalized === '0') return 'pending';
    
    console.log('Unknown status format:', status, 'normalized to pending');
    return 'pending';
  }, []);

  // Helper function to safely access localStorage
  const safeLocalStorage = {
    getItem: (key) => {
      try {
        return typeof window !== 'undefined' ? localStorage.getItem(key) : null;
      } catch (error) {
        console.warn('localStorage not available:', error);
        return null;
      }
    },
    setItem: (key, value) => {
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem(key, value);
        }
      } catch (error) {
        console.warn('localStorage not available:', error);
      }
    },
    removeItem: (key) => {
      try {
        if (typeof window !== 'undefined') {
          localStorage.removeItem(key);
        }
      } catch (error) {
        console.warn('localStorage not available:', error);
      }
    }
  };

  // Default avatar function
  const getDefaultAvatar = useCallback((name) => {
    // Create a simple SVG avatar with user initials
    const initials = name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U';
    const colors = ['#f59e0b', '#ef4444', '#10b981', '#3b82f6', '#ce55b9', '#f97316', '#06b6d4', '#84cc16'];
    const colorIndex = name ? name.charCodeAt(0) % colors.length : 0;
    const bgColor = colors[colorIndex];
    
    return `data:image/svg+xml;base64,${btoa(`
      <svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
        <rect width="48" height="48" fill="${bgColor}" rx="24"/>
        <text x="24" y="32" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="16" font-weight="bold">
          ${initials}
        </text>
      </svg>
    `)}`;
  }, []);

  // State declarations
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(true);
  const [isContactVisible, setIsContactVisible] = useState(false);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  
  // Enhanced state for submitted offer with more details
  const [submittedOffer, setSubmittedOffer] = useState(null);
  const [offerStatusHistory, setOfferStatusHistory] = useState([]);

  // Updated state for buyer requests from API response
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserLoading, setCurrentUserLoading] = useState(true);

  // Add polling state for status updates
  const [isPollingStatus, setIsPollingStatus] = useState(false);

  // Buy Now Modal State
  const [isBuyNowModalOpen, setIsBuyNowModalOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showBuyNowForm, setShowBuyNowForm] = useState(false);
  const [showOnlinePaymentOptions, setShowOnlinePaymentOptions] = useState(false);
  const [buyNowStep, setBuyNowStep] = useState('payment-method');
  const [isAcceptedOfferPurchase, setIsAcceptedOfferPurchase] = useState(false);
  const [buyNowForm, setBuyNowForm] = useState({
    name: '',
    pickup_location: '',
    pickup_date: getCurrentDate(),
    pickup_time: getCurrentTime()
  });
  const [isSubmittingBuyNow, setIsSubmittingBuyNow] = useState(false);

  // Offer Modal State
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
  const [offerForm, setOfferForm] = useState({
    name: '',
    pickup_location: '',
    amount: '',
    pickup_date: getCurrentDate(),
    pickup_time: getCurrentTime()
  });
  const [isSubmittingOffer, setIsSubmittingOffer] = useState(false);

  // Helper function to get submitted offer storage key
  const getSubmittedOfferKey = useCallback((itemId) => {
    return `marketplace_submitted_offer_${itemId}`;
  }, []);

  // Helper function to get offer status history storage key
  const getOfferStatusHistoryKey = useCallback((itemId) => {
    return `marketplace_offer_status_history_${itemId}`;
  }, []);

  // Helper function to load submitted offer from localStorage
  const loadSubmittedOffer = useCallback((itemId) => {
    if (!itemId) return null;
    
    try {
      const storedOffer = safeLocalStorage.getItem(getSubmittedOfferKey(itemId));
      if (storedOffer) {
        return JSON.parse(storedOffer);
      }
    } catch (error) {
      console.warn('Error loading submitted offer from localStorage:', error);
      safeLocalStorage.removeItem(getSubmittedOfferKey(itemId));
    }
    return null;
  }, [getSubmittedOfferKey]);

  // Helper function to save submitted offer to localStorage
  const saveSubmittedOffer = useCallback((itemId, offerData) => {
    if (!itemId || !offerData) return;
    
    try {
      safeLocalStorage.setItem(getSubmittedOfferKey(itemId), JSON.stringify(offerData));
    } catch (error) {
      console.warn('Error saving submitted offer to localStorage:', error);
    }
  }, [getSubmittedOfferKey]);

  // Helper function to remove submitted offer from localStorage
  const removeSubmittedOffer = useCallback((itemId) => {
    if (!itemId) return;
    
    try {
      safeLocalStorage.removeItem(getSubmittedOfferKey(itemId));
      safeLocalStorage.removeItem(getOfferStatusHistoryKey(itemId));
    } catch (error) {
      console.warn('Error removing submitted offer from localStorage:', error);
    }
  }, [getSubmittedOfferKey, getOfferStatusHistoryKey]);

  // Helper function to add status to history
  const addStatusToHistory = useCallback((itemId, status, timestamp = null) => {
    if (!itemId || !status) return;
    
    try {
      const historyKey = getOfferStatusHistoryKey(itemId);
      const existingHistory = JSON.parse(safeLocalStorage.getItem(historyKey) || '[]');
      
      const newEntry = {
        status,
        timestamp: timestamp || new Date().toISOString(),
        id: Date.now()
      };
      
      // Avoid duplicates
      const lastEntry = existingHistory[existingHistory.length - 1];
      if (!lastEntry || lastEntry.status !== status) {
        existingHistory.push(newEntry);
        safeLocalStorage.setItem(historyKey, JSON.stringify(existingHistory));
        if (isMountedRef.current) {
          setOfferStatusHistory(existingHistory);
        }
      }
    } catch (error) {
      console.warn('Error adding status to history:', error);
    }
  }, [getOfferStatusHistoryKey]);

  // Enhanced payment method validation
  const validatePaymentMethods = useCallback(() => {
    if (!item) return { paypal: { available: false }, venmo: { available: false } };
    
    const validation = {
      paypal: {
        available: false,
        field: null,
        value: null,
        issues: []
      },
      venmo: {
        available: false,
        field: null,
        value: null,
        issues: []
      }
    };

    // Check PayPal
    const paypalFields = ['paypal_id', 'paypal_link', 'paypal_url', 'payment_paypal', 'paypal_payment_id'];
    for (const field of paypalFields) {
      if (item[field] && item[field].trim()) {
        validation.paypal.available = true;
        validation.paypal.field = field;
        validation.paypal.value = item[field];
        break;
      }
    }

    // Fallback to payment_id if payment_option is 2 (PayPal)
    if (!validation.paypal.available && item.payment_option === 2 && item.payment_id) {
      validation.paypal.available = true;
      validation.paypal.field = 'payment_id';
      validation.paypal.value = item.payment_id;
    }

    // Check Venmo
    const venmoFields = ['venmo_id', 'venmo_link', 'venmo_url', 'payment_venmo', 'venmo_payment_id', 'venmo_username'];
    for (const field of venmoFields) {
      if (item[field] && item[field].trim()) {
        validation.venmo.available = true;
        validation.venmo.field = field;
        validation.venmo.value = item[field];
        break;
      }
    }

    // Fallback to payment_id if payment_option is 1 (Venmo)
    if (!validation.venmo.available && item.payment_option === 1 && item.payment_id) {
      validation.venmo.available = true;
      validation.venmo.field = 'payment_id';
      validation.venmo.value = item.payment_id;
    }

    // Validate PayPal format
    if (validation.paypal.available) {
      const paypalValue = validation.paypal.value;
      if (!paypalValue.includes('paypal') && !paypalValue.includes('@')) {
        validation.paypal.issues.push('PayPal ID should contain "paypal" or be an email');
      }
    }

    // Validate Venmo format
    if (validation.venmo.available) {
      const venmoValue = validation.venmo.value;
      if (!venmoValue.includes('venmo') && !venmoValue.startsWith('@')) {
        validation.venmo.issues.push('Venmo ID should contain "venmo" or start with @');
      }
    }

    console.log('💳 Payment Method Validation:', validation);
    return validation;
  }, [item]);

  // Helper function to check if payment methods are available
  const getAvailablePaymentMethods = useCallback(() => {
    if (!item) return [];
    
    const methods = [];
    const validation = validatePaymentMethods();
    
    if (validation.paypal.available) methods.push('paypal');
    if (validation.venmo.available) methods.push('venmo');
    
    return methods;
  }, [item, validatePaymentMethods]);

  // Check wishlist status
 const checkWishlistStatus = useCallback(async (itemId) => {
  if (!itemId) return;
  
  try {
    const wishlist = JSON.parse(safeLocalStorage.getItem('marketplaceWishlist') || '[]');
    
    // Since we're on the favorites details page, this item should be in favorites
    // Check if it exists in the wishlist
    const isInWishlist = wishlist.includes(parseInt(itemId)) || 
                        wishlist.includes(itemId.toString()) ||
                        wishlist.includes(itemId);
    
    console.log('🔍 Wishlist Check:', {
      itemId,
      wishlist,
      isInWishlist,
      itemIdType: typeof itemId,
      wishlistItemTypes: wishlist.map(id => typeof id)
    });
    
    if (isMountedRef.current) {
      // Since this is the favorites details page, default to true if item should be favorited
      // If not found in wishlist, add it since user navigated here from favorites
      if (!isInWishlist) {
        console.log('📌 Item not in wishlist but on favorites page - adding to wishlist');
        const updatedWishlist = [...wishlist, itemId];
        safeLocalStorage.setItem('marketplaceWishlist', JSON.stringify(updatedWishlist));
        setIsWishlisted(true);
      } else {
        setIsWishlisted(true);
      }
    }
  } catch (error) {
    console.error('Error checking wishlist status:', error);
    // On favorites details page, default to true even on error
    if (isMountedRef.current) {
      setIsWishlisted(true);
    }
  }
}, []);

  // FIXED: Navigate back to favorites function
  const navigateBackToFavorites = useCallback(() => {
    navigate('/myaccount/favorite');
  }, [navigate]);

  // Toggle wishlist
  const toggleWishlist = useCallback(async () => {
    if (!item?.id) return;
    
    try {
      if (isWishlisted) {
        const wishlist = JSON.parse(safeLocalStorage.getItem('marketplaceWishlist') || '[]');
        // Remove both string and number versions to ensure clean removal
        const updatedWishlist = wishlist.filter(wishId => 
          wishId !== item.id && 
          wishId !== parseInt(item.id) && 
          wishId !== item.id.toString()
        );
        safeLocalStorage.setItem('marketplaceWishlist', JSON.stringify(updatedWishlist));
        setIsWishlisted(false);
        toast.success('Removed from favorites');
        
        // Optional: Navigate back to favorites page after removal
        setTimeout(() => {
          if (window.confirm('Item removed from favorites. Would you like to go back to your favorites list?')) {
            navigateBackToFavorites();
          }
        }, 1500);
      } else {
        const wishlist = JSON.parse(safeLocalStorage.getItem('marketplaceWishlist') || '[]');
        // Ensure we're not adding duplicates (check both string and number)
        const itemExists = wishlist.some(wishId => 
          wishId === item.id || 
          wishId === parseInt(item.id) || 
          wishId === item.id.toString()
        );
        
        if (!itemExists) {
          // Store as the same type as the item.id
          wishlist.push(item.id);
          safeLocalStorage.setItem('marketplaceWishlist', JSON.stringify(wishlist));
        }
        setIsWishlisted(true);
        toast.success('Added to favorites');
      }
    } catch (error) {
      console.error('Error updating wishlist:', error);
      toast.error('Failed to update favorites');
    }
  }, [item?.id, isWishlisted, navigateBackToFavorites]);

  // Safe image navigation functions
  const nextImage = useCallback(() => {
    if (!item?.images || item.images.length === 0) return;
    setCurrentImageIndex(prevIndex =>
      prevIndex === item.images.length - 1 ? 0 : prevIndex + 1
    );
  }, [item?.images]);

  const prevImage = useCallback(() => {
    if (!item?.images || item.images.length === 0) return;
    setCurrentImageIndex(prevIndex =>
      prevIndex === 0 ? item.images.length - 1 : prevIndex - 1
    );
  }, [item?.images]);

  const goToImage = useCallback((index) => {
    if (!item?.images || index < 0 || index >= item.images.length) return;
    setCurrentImageIndex(index);
  }, [item?.images]);

  // Chat handler
  const handleChat = useCallback((sellerId) => {
    if (!sellerId || !item) return;
    
    try {
      // Prepare product details to pass to chat
      const productDetails = {
        productName: item.title,
        productPrice: item.price,
        productLink: window.location.href,
        productId: item.id,
        productImage: item.images && item.images.length > 0 ? item.images[0].image_url : '',
        sellerName: item.seller?.name || 'Seller'
      };
      
      // Create URL parameters
      const queryParams = new URLSearchParams({
        user_id: sellerId,
        product_name: productDetails.productName,
        product_price: productDetails.productPrice,
        product_link: productDetails.productLink,
        product_id: productDetails.productId,
        product_image: productDetails.productImage,
        seller_name: productDetails.sellerName,
        auto_message: 'true'
      });
      
      // Navigate to chat with product details
      navigate(`/myaccount/chat?${queryParams.toString()}`);
      
      toast.info('Opening chat with seller...', {
        position: "top-center",
        autoClose: 2000,
      });
      
    } catch (error) {
      console.error('Error navigating to chat:', error);
      toast.error('Failed to open chat. Please try again.', {
        position: "top-center",
        autoClose: 3000,
      });
    }
  }, [item, navigate]);

  const handleMakeOffer = useCallback(() => {
    setIsOfferModalOpen(true);
  }, []);

  const handleBuyNow = useCallback(() => {
    setIsBuyNowModalOpen(true);
    setSelectedPaymentMethod(null);
    setBuyNowStep('payment-method');
    setIsAcceptedOfferPurchase(false);
  }, []);

  const closeBuyNowModal = useCallback(() => {
    setIsBuyNowModalOpen(false);
    setSelectedPaymentMethod(null);
    setIsProcessingPayment(false);
    setShowBuyNowForm(false);
    setShowOnlinePaymentOptions(false);
    setBuyNowStep('payment-method');
    setIsAcceptedOfferPurchase(false);
    setBuyNowForm({
      name: '',
      pickup_location: '',
      pickup_date: getCurrentDate(),
      pickup_time: getCurrentTime()
    });
    setIsSubmittingBuyNow(false);
  }, []);

  const closeOfferModal = useCallback(() => {
    setIsOfferModalOpen(false);
    setOfferForm({
      name: '',
      pickup_location: '',
      amount: '',
      pickup_date: getCurrentDate(),
      pickup_time: getCurrentTime()
    });
  }, []);

  // Set page title for favorites context
  useEffect(() => {
    if (item) {
      document.title = `${item.title} - Favorites Details`;
    }
    
    return () => {
      document.title = 'Favorites Details';
    };
  }, [item]);

  // Fetch item details
  useEffect(() => {
    let mounted = true;
    
    const fetchItemDetails = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/detail/${id}`
        );

        if (!mounted) return;

        if (response.data.status !== 200) {
          throw new Error(response.data.message || 'Failed to fetch item details');
        }

        const itemData = response.data.data;
        
        console.log('📊 Item data fields:', Object.keys(itemData));
        
        if (itemData.title) {
          const correctSlug = createSlug(itemData.title);
          if (!slug || slug !== correctSlug) {
            // Update URL without triggering a re-render
            const newUrl = `/myaccount/favorites/${id}/${correctSlug}`;
            window.history.replaceState(null, '', newUrl);
          }
        }
        
        setItem(itemData);
        setCurrentImageIndex(0);
        
        // Check if payment has already been made
        if (itemData.is_paid === 1) {
          toast.success('✅ Payment already completed for this item!', {
            position: "top-center",
            autoClose: 4000,
          });
        }
        
        await checkWishlistStatus(itemData.id);

        // Load submitted offer and status history
        const storedOffer = loadSubmittedOffer(itemData.id);
        if (storedOffer && mounted) {
          setSubmittedOffer(storedOffer);
          
          // Load status history
          try {
            const historyKey = getOfferStatusHistoryKey(itemData.id);
            const history = JSON.parse(safeLocalStorage.getItem(historyKey) || '[]');
            setOfferStatusHistory(history);
          } catch (error) {
            console.warn('Error loading status history:', error);
          }
        }
      } catch (err) {
        if (mounted) {
          setError(err.message);
          console.error('Error fetching item details:', err);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchItemDetails();
    
    return () => {
      mounted = false;
    };
  }, [id, slug, createSlug, checkWishlistStatus, loadSubmittedOffer, getOfferStatusHistoryKey]);

  // Cleanup effect
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // Early returns for loading/error states
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <img 
          src={loadingGif} 
          alt="Loading..." 
          style={{ width: '80px' }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 max-w-4xl mx-auto">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
          <button 
            onClick={navigateBackToFavorites}
            className="mt-2 inline-block text-blue-600 hover:text-blue-800 cursor-pointer underline"
          >
            ← Back to Favorites
          </button>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="p-4 max-w-4xl mx-auto">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative">
          <strong className="font-bold">Not Found</strong>
          <span className="block sm:inline"> Item with ID {id} not found.</span>
          <button 
            onClick={navigateBackToFavorites}
            className="mt-2 inline-block text-blue-600 hover:text-blue-800 cursor-pointer underline"
          >
            ← Back to Favorites
          </button>
        </div>
      </div>
    );
  }

  const currentImage = item.images && item.images.length > 0 ? item.images[currentImageIndex] : null;

  return (
    <div className="favorites-details container mx-auto px-4 py-8">
      {/* FIXED: Back to Favorites button */}
      <button 
        onClick={navigateBackToFavorites}
        className="inline-flex items-center mb-6 text-blue-600 hover:text-blue-800 cursor-pointer"
      >
        <FaChevronLeft className="mr-2" /> Back to Favorites
      </button>

      {/* Favorites Context Banner */}
      <div className="mb-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-center">
          <FaHeart className="text-purple-500 text-xl mr-3" />
          <div className="flex-1">
            <h3 className="text-purple-800 font-semibold">Viewing from your Favorites</h3>
            <p className="text-purple-700 text-sm mt-1">
              This item is in your favorites list. You can view details, make offers, and purchase directly from here.
            </p>
          </div>
          <button
            onClick={toggleWishlist}
            className="ml-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm font-medium"
          >
            Remove from Favorites
          </button>
        </div>
      </div>

      {/* Rest of the component remains largely the same */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <span className="inline-block px-3 py-1 text-sm font-semibold text-gray-700 bg-gray-100 rounded-full mb-2">
                    {item.category_name || 'Uncategorized'}
                  </span>
                  <h1 className="text-3xl font-bold text-gray-900">{item.title}</h1>
                </div>
                <button
                  onClick={toggleWishlist}
                  className="text-2xl focus:outline-none"
                  aria-label={isWishlisted ? "Remove from favorites" : "Add to favorites"}
                >
                  {isWishlisted ? (
                    <FaHeart className="text-red-500" />
                  ) : (
                    <FaRegHeart className="text-gray-400 hover:text-red-500" />
                  )}
                </button>
              </div>

              <div className="flex items-center text-sm text-gray-500 mb-6">
                <span className="flex items-center mr-4">
                 <FaCalendarAlt className="w-3 h-3 mr-1" style={{ color: '#9e9e9eff' }} />
                  Posted {item.posted_on || 'Unknown'} &nbsp; &nbsp;&nbsp;&nbsp;&nbsp;
                  👁 Views: {item.views || 0} 
                </span>
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1" viewBox="0 0 512 512">
                    <path d="M393,450a14.92,14.92,0,0,1-7.46-2L257,374.29,128.46,448A15,15,0,0,1,106,435V63a15,15,0,0,1,15-15H393a15,15,0,0,1,15,15V435a15,15,0,0,1-15,15ZM257,342a14.92,14.92,0,0,1,7.46,2L378,409.1V78H136V409.1L249.54,344A14.92,14.92,0,0,1,257,342Z" />
                  </svg>
                  ID: #{id}
                </span>
              </div>

              {/* Image Gallery */}
              <div className="relative mb-6">
                {currentImage ? (
                  <div className="space-y-4">
                    <div className="relative">
                      <img
                        src={currentImage.image_url}
                        alt={item.title}
                        className="w-full h-96 object-contain rounded-lg bg-gray-100"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-96 bg-gray-100 rounded-lg hidden items-center justify-center text-gray-500">
                        Image not available
                      </div>

                      <button
                        onClick={() => setIsZoomOpen(true)}
                        className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition"
                        aria-label="Zoom Image"
                      >
                        <FaSearchPlus />
                      </button>

                      {item.images && item.images.length > 1 && (
                        <>
                          <button
                            onClick={prevImage}
                            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition"
                          >
                            <FaChevronLeft />
                          </button>
                          <button
                            onClick={nextImage}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition"
                          >
                            <FaChevronRight />
                          </button>
                        </>
                      )}
                    </div>
                    {item.images && item.images.length > 1 && (
                      <div className="flex justify-center space-x-2">
                        {item.images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => goToImage(index)}
                            className={`w-3 h-3 rounded-full ${currentImageIndex === index ? 'bg-blue-500' : 'bg-gray-300'}`}
                            aria-label={`Go to image ${index + 1}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-full h-96 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500">
                    No image available
                  </div>
                )}
              </div>

              {/* Zoom Modal */}
              {isZoomOpen && currentImage && (
                <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50">
                  <div className="relative max-w-4xl w-full p-4 flex justify-center items-center">
                    <button
                      onClick={() => setIsZoomOpen(false)}
                      className="absolute top-4 right-4 text-white text-2xl hover:text-gray-300"
                    >
                      <FaTimes />
                    </button>

                    {item.images && item.images.length > 1 && (
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition"
                      >
                        <FaChevronLeft />
                      </button>
                    )}

                    <img
                      src={currentImage.image_url}
                      alt={item.title}
                      className="w-full max-h-[80vh] object-contain mx-auto"
                    />

                    {item.images && item.images.length > 1 && (
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition"
                      >
                        <FaChevronRight />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Description Section */}
              <div className="mb-8">
                <h2 className="text-xl font-bold mb-4 text-gray-800">Description</h2>
                <p className="text-gray-700 whitespace-pre-line">{item.description || 'No description available'}</p>
              </div>
            </div>
          </div>

          {/* Additional Details */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
            <div className='p-6'>
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Item Details</h3>
              <ul className="space-y-2">
                <li className="flex justify-between">
                  <span className="text-gray-600">Condition</span>
                  <span className="font-medium">{getItemCondition(item)}</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-gray-600">Quantity</span>
                  <span className="font-medium">{item.quantity || 1}</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className={`font-medium ${item.is_sold ? 'text-red-500' : 'text-green-500'}`}>
                    {item.is_sold ? 'Sold' : 'Available'}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Right Column - Price and Actions */}
        <div className="lg:col-span-1">
          <div className="right-price-op relative sitebg from-purple-500 to-pink-500 rounded-2xl shadow-xl text-white sticky top-4 overflow-hidden p-6 space-y-4">
            <div className="absolute top-2 right-2 bg-yellow-300 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full shadow">
              Favorited Item
            </div>

            <div className="text-center">
              <h2 className="text-5xl font-extrabold">$ {item.price || 0}</h2>
              <p className="text-sm mt-1 opacity-80">From your favorites!</p>
              
              {/* Pickup Location Display */}
              {item.location && (
                <div className="mt-3 bg-white bg-opacity-20 rounded-lg p-2">
                  <div className="flex items-center justify-center text-sm">
                    <FaMapMarkerAlt className="mr-2 text-purple-600" />
                    <div>
                      <p className="text-xs text-gray-600 opacity-90">Pickup Location</p>
                      <p className="font-medium text-gray-800">{item.location}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {!item.is_sold && item.is_paid !== 1 ? (
              <div className="space-y-3 pt-4">
                <button
                  onClick={() => navigate(`/marketplace/${id}/${slug || createSlug(item.title)}`)}
                  className="w-full bg-white text-purple-600 font-semibold py-2 px-4 rounded-lg hover:bg-gray-100 transition"
                >
                  View in Marketplace
                </button>
                
                <button
                  onClick={handleMakeOffer}
                  className="w-full bg-yellow-300 text-black font-semibold py-2 px-4 rounded-lg hover:bg-yellow-400 transition"
                >
                  Make an Offer
                </button>
                
                <button
                  onClick={handleBuyNow}
                  className="buy-n w-full border border-white text-white font-semibold py-2 px-4 rounded-lg hover:bg-white hover:text-purple-600 transition"
                >
                  Buy Now
                </button>
              </div>
            ) : item.is_paid === 1 ? (
              <div className="text-center bg-blue-100 text-blue-800 p-3 rounded-lg pt-4">
                <div className="flex items-center justify-center mb-2">
                  <FaCheckCircle className="text-blue-600 mr-2" />
                  <span className="font-semibold">Payment Completed</span>
                </div>
                <p className="text-sm">
                  Your payment has been recorded.
                </p>
              </div>
            ) : (
              <div className="text-center text-yellow-200 font-semibold pt-4">
                {item.is_sold ? 'Item Sold' : 'Unavailable'}
              </div>
            )}
          </div>

          {/* Seller Information */}
          <div className='bg-white rounded-lg shadow-md overflow-hidden sticky top-4 mt-3'>
            <div className='p-6'>
              <div className="">
                {!item.is_sold && (
                  <button
                    onClick={() => setIsContactVisible(!isContactVisible)}
                    className=" float-end text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    {isContactVisible ? 'Hide Contact Info' : 'Show Contact Info'}
                  </button>
                )}
                <h3 className="text-lg font-semibold mb-4 text-gray-800">Seller Information</h3>
                <div className="flex items-start space-x-4 mb-4">
                  <div className="flex-shrink-0">
                    {item.seller?.profile_picture ? (
                      <img
                        src={item.seller.profile_picture}
                        alt={item.seller?.name || 'Seller'}
                        className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                        onError={(e) => {
                          e.target.src = getDefaultAvatar(item.seller?.name || 'User');
                        }}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center border-2 border-gray-200">
                        <FaUser className="text-gray-600 text-lg" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{item.seller?.name || 'Unknown Seller'}</p>
                    <p className="text-sm text-gray-500">Member since {item.seller?.member_since || 'Unknown'}</p>
                    {item.seller?.rating && (
                      <div className="flex items-center mt-1">
                        <div className="flex text-yellow-400">
                          {[...Array(5)].map((_, i) => (
                            <svg
                              key={i}
                              className={`w-4 h-4 ${i < Math.floor(item.seller.rating) ? 'fill-current' : 'stroke-current'}`}
                              viewBox="0 0 24 24"
                            >
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                          ))}
                        </div>
                        <span className="text-xs text-gray-500 ml-1">{item.seller.rating.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {isContactVisible && item.seller && (
                  <div className="space-y-3 mt-4">
                    {item.seller.phone_number && (
                      <div className="flex items-center text-gray-700">
                        <FaPhone className="text-gray-500 mr-2" />
                        <a href={`tel:${item.seller.phone_number}`} className="hover:text-blue-600">
                          {item.seller.phone_number}
                        </a>
                      </div>
                    )}
                    {item.seller.email && (
                      <div className="flex items-center text-gray-700">
                        <FaEnvelope className="text-gray-500 mr-2" />
                        <a href={`mailto:${item.seller.email}`} className="hover:text-blue-600">
                          {item.seller.email}
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Safety Tips */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden mt-6">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Safety Tips</h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex items-start">
                  <svg className="w-4 h-4 mt-1 mr-2 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Meet in a public place
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 mt-1 mr-2 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Check the item before buying
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 mt-1 mr-2 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Never pay in advance
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 mt-1 mr-2 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Use secure payment methods
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Basic Modals for Offer and Buy Now */}
      {/* Make an Offer Modal */}
      {isOfferModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">Make an Offer</h3>
                <button
                  onClick={closeOfferModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes />
                </button>
              </div>
              <p className="text-gray-600 mb-4">
                This will redirect you to the marketplace to complete your offer.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={closeOfferModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => navigate(`/marketplace/${id}/${slug || createSlug(item.title)}`)}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition"
                >
                  Go to Marketplace
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Buy Now Modal */}
      {isBuyNowModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">Buy Now</h3>
                <button
                  onClick={closeBuyNowModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes />
                </button>
              </div>
              <p className="text-gray-600 mb-4">
                This will redirect you to the marketplace to complete your purchase.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={closeBuyNowModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => navigate(`/marketplace/${id}/${slug || createSlug(item.title)}`)}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition"
                >
                  Go to Marketplace
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FavoritesDetailsPage;